/**
 * Assistant Page - AI-powered suggestions and help
 *
 * Features:
 * - Active suggestions with dismiss
 * - Quick stats overview
 * - Contextual help
 */

import { useWorkspace } from '../context/WorkspaceContext';
import {
  getActiveSuggestions,
  dismissSuggestion,
  getAssistantHome,
} from '../lib/services/assistant.service';
import {
  SparklesIcon,
  LightBulbIcon,
  ClockIcon,
  CheckCircleIcon,
  XMarkIcon,
  BellIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';
import type { AssistantSuggestion, SuggestionType } from '../lib/types';

const SUGGESTION_TYPE_CONFIG: Record<SuggestionType, { label: string; color: string; icon: typeof LightBulbIcon }> = {
  task: { label: 'Task', color: 'blue', icon: CheckCircleIcon },
  follow_up: { label: 'Follow-up', color: 'purple', icon: ClockIcon },
  reminder: { label: 'Reminder', color: 'amber', icon: BellIcon },
  optimization: { label: 'Optimization', color: 'green', icon: ArrowPathIcon },
  insight: { label: 'Insight', color: 'indigo', icon: LightBulbIcon },
};

export function AssistantPage() {
  const { currentWorkspace, refreshWorkspaces } = useWorkspace();

  if (!currentWorkspace) return null;

  const suggestions = getActiveSuggestions(currentWorkspace.id);
  const homeData = getAssistantHome(currentWorkspace.id);

  const handleDismiss = (suggestionId: string) => {
    dismissSuggestion(suggestionId);
    refreshWorkspaces();
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-3 rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 text-white">
          <SparklesIcon className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI Assistant</h1>
          <p className="text-sm text-gray-500">{homeData.greeting}</p>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="card-compact text-center">
          <p className="text-3xl font-bold text-gray-900">{homeData.quickStats.pendingTasks}</p>
          <p className="text-sm text-gray-500">Pending Tasks</p>
        </div>
        <div className="card-compact text-center">
          <p className="text-3xl font-bold text-gray-900">{homeData.quickStats.newLeads}</p>
          <p className="text-sm text-gray-500">New Leads</p>
        </div>
        <div className="card-compact text-center">
          <p className="text-3xl font-bold text-primary-600">{homeData.quickStats.urgentItems}</p>
          <p className="text-sm text-gray-500">Urgent Items</p>
        </div>
      </div>

      {/* Active Suggestions */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Active Suggestions</h2>
          <span className="text-sm text-gray-500">{suggestions.length} suggestions</span>
        </div>

        {suggestions.length === 0 ? (
          <div className="card text-center py-12">
            <SparklesIcon className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">All caught up!</h3>
            <p className="text-sm text-gray-500">
              No active suggestions right now. Keep adding data and the AI will provide personalized recommendations.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {suggestions.map((suggestion) => (
              <SuggestionCard
                key={suggestion.id}
                suggestion={suggestion}
                onDismiss={() => handleDismiss(suggestion.id)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Tips Section */}
      <div className="card bg-gradient-to-r from-primary-50 to-primary-100/50 border-primary-200">
        <div className="flex items-start gap-4">
          <div className="p-2 rounded-lg bg-primary-100 text-primary-600">
            <ChatBubbleLeftRightIcon className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-primary-900">Pro Tips</h3>
            <ul className="mt-2 space-y-2 text-sm text-primary-800">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 flex-shrink-0" />
                <span>Keep tasks moving through the pipeline - items in Triage should be processed daily</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 flex-shrink-0" />
                <span>Follow up with leads within 24 hours for best conversion rates</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 flex-shrink-0" />
                <span>Use notes to document meeting outcomes and key decisions</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-primary-500 mt-1.5 flex-shrink-0" />
                <span>Check Insights regularly to spot trends and opportunities</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Help Section */}
      <div className="grid md:grid-cols-2 gap-4">
        <HelpCard
          title="Getting Started"
          description="Learn the basics of managing your workspace effectively"
          icon={SparklesIcon}
        />
        <HelpCard
          title="Keyboard Shortcuts"
          description="Speed up your workflow with these handy shortcuts"
          icon={LightBulbIcon}
        />
      </div>
    </div>
  );
}

function SuggestionCard({
  suggestion,
  onDismiss,
}: {
  suggestion: AssistantSuggestion;
  onDismiss: () => void;
}) {
  const config = SUGGESTION_TYPE_CONFIG[suggestion.type];
  const Icon = config.icon;

  const priorityStyles: Record<string, string> = {
    low: 'border-l-gray-300',
    medium: 'border-l-blue-400',
    high: 'border-l-amber-400',
  };

  return (
    <div className={clsx('card border-l-4', priorityStyles[suggestion.priority || 'medium'] || 'border-l-blue-400')}>
      <div className="flex items-start gap-3">
        <div className={clsx(
          'p-2 rounded-lg flex-shrink-0',
          config.color === 'blue' ? 'bg-blue-100 text-blue-600' :
          config.color === 'purple' ? 'bg-purple-100 text-purple-600' :
          config.color === 'amber' ? 'bg-amber-100 text-amber-600' :
          config.color === 'green' ? 'bg-green-100 text-green-600' :
          'bg-indigo-100 text-indigo-600'
        )}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={clsx(
              'badge',
              config.color === 'blue' ? 'badge-info' :
              config.color === 'purple' ? 'badge-purple' :
              config.color === 'amber' ? 'badge-warning' :
              config.color === 'green' ? 'badge-success' :
              'badge-primary'
            )}>
              {config.label}
            </span>
            {suggestion.priority === 'high' && (
              <span className="badge badge-danger">High Priority</span>
            )}
          </div>
          <h3 className="text-sm font-semibold text-gray-900">{suggestion.title}</h3>
          <p className="text-sm text-gray-600 mt-1">{suggestion.description}</p>
        </div>
        <button
          onClick={onDismiss}
          className="btn-icon flex-shrink-0"
          title="Dismiss"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}

function HelpCard({
  title,
  description,
  icon: Icon,
}: {
  title: string;
  description: string;
  icon: typeof SparklesIcon;
}) {
  return (
    <div className="card-interactive hover:border-primary-200">
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-gray-100 text-gray-600">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500 mt-1">{description}</p>
        </div>
      </div>
    </div>
  );
}

export default AssistantPage;
